import { useRef, useEffect } from 'react';
import { useI18n } from '../i18n/I18nContext';
import { skills } from '../data/skills';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export function ThoughtProcess() {
  const { t } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);
  const lifeSkills = skills.filter(s => s.category === 'life');
  const techSkills = skills.filter(s => s.category === 'tech');
  const aiSkills = skills.filter(s => s.category === 'ai');

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1,
        },
      });

      const thoughtNodes = containerRef.current?.querySelectorAll('.thought-node');
      if (thoughtNodes) {
        tl.fromTo(
          thoughtNodes,
          { opacity: 0, scale: 0.5, y: 50 },
          { opacity: 1, scale: 1, y: 0, duration: 1, stagger: 0.15, ease: 'power2.out' },
          0
        );
      }

      const connectionLines = containerRef.current?.querySelectorAll('.connection-line');
      if (connectionLines) {
        tl.fromTo(
          connectionLines,
          { scaleX: 0, transformOrigin: 'left center' },
          { scaleX: 1, duration: 1, stagger: 0.1, ease: 'power2.out' },
          0.3
        );
      }
    }, containerRef);

    return () => ctx.revert();
  }, []);

  const renderSkillGroup = (groupSkills: typeof skills, groupTitle: string, _delay: number) => (
    <div
      key={groupTitle}
      className="skill-group"
      style={{
        opacity: 0,
        animation: `fadeInUp 0.8s ease-out ${_delay}s forwards`,
      }}
    >
      <h3
        className="group-title"
        style={{
          fontSize: '0.75rem',
          color: 'var(--fg-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.2em',
          marginBottom: '1.5rem',
          fontWeight: 500,
        }}
      >
        {t(groupTitle)}
      </h3>
      <div
        className="skill-nodes"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1rem',
          justifyContent: 'center',
        }}
      >
        {groupSkills.map((skill) => (
          <div
            key={skill.id}
            className="thought-node"
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '1.5rem 2rem',
              background: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
              borderRadius: '12px',
              minWidth: '140px',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            }}
            onMouseEnter={e => {
              gsap.to(e.currentTarget, { y: -8, boxShadow: '0 20px 40px rgba(0, 255, 136, 0.15)', duration: 0.3 });
            }}
            onMouseLeave={e => {
              gsap.to(e.currentTarget, { y: 0, boxShadow: 'none', duration: 0.3 });
            }}
          >
            <span
              className="skill-icon"
              style={{
                fontSize: '2.5rem',
                filter: 'drop-shadow(0 0 20px currentColor)',
              }}
            >
              {skill.icon}
            </span>
            <span
              className="skill-label"
              style={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'var(--fg-primary)',
                textAlign: 'center',
              }}
            >
              {t(skill.labelKey)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section
      ref={containerRef}
      id="thoughts"
      className="thought-process"
      style={{
        minHeight: '100vh',
        padding: '6rem 2rem',
        position: 'relative',
      }}
      aria-labelledby="thoughts-title"
    >
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .connection-line {
          width: 60px;
          height: 2px;
          background: linear-gradient(90deg, var(--accent-primary), var(--accent-secondary));
          margin: 0 auto;
          border-radius: 1px;
        }
      `}</style>

      <div className="bg-pattern" style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: 'radial-gradient(ellipse at center, rgba(0, 255, 136, 0.03) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <header className="section-header" style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <h2 id="thoughts-title" className="section-title" style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: 700,
            marginBottom: '1rem',
            background: 'linear-gradient(135deg, var(--fg-primary), var(--accent-primary))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            {t('thoughts.title')}
          </h2>
          <p className="section-subtitle" style={{
            fontSize: 'clamp(1rem, 2vw, 1.25rem)',
            color: 'var(--fg-secondary)',
            maxWidth: '600px',
            margin: '0 auto',
          }}>
            {t('thoughts.subtitle')}
          </p>
        </header>

        <div className="skill-groups" style={{ display: 'flex', flexDirection: 'column', gap: '4rem' }}>
          {renderSkillGroup(lifeSkills, 'skills.categories.life', 0)}
          <div className="connection-line" style={{ width: '80px', transform: 'rotate(90deg)', margin: '0 auto' }} />
          {renderSkillGroup(techSkills, 'skills.categories.tech', 0.2)}
          <div className="connection-line" style={{ width: '80px', transform: 'rotate(90deg)', margin: '0 auto' }} />
          {renderSkillGroup(aiSkills, 'skills.categories.ai', 0.4)}
        </div>
      </div>
    </section>
  );
}